import React, {
  useEffect,
  useState,
  useLayoutEffect,
  forwardRef,
  useRef,
  useImperativeHandle,
} from 'react';
import { useParams, useHistory } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { getTickets } from 'src/actions/queueActions';
import { CirclePicker } from 'react-color';
import axios from 'src/utils/axios';

import {
  Divider,
  makeStyles,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TableHead,
  Grid,
  CircularProgress,
  TableContainer,
  TablePagination,
  Link,
  Typography,
  Dialog,
  DialogContent,
  Button,
  DialogActions,
  Backdrop,
} from '@material-ui/core';
import { CSVLink } from 'react-csv';
import useQuery from 'src/utils/useQuery';
import useLocalStorage from 'src/utils/useLocalStorage';
import Toolbar from './Toolbar';
import QueueItem from './QueueItem';

const useStyles = makeStyles((theme) => ({
  root: {
    flexGrow: 1,
    overflow: 'hidden',
    backgroundColor: theme.palette.background.dark,
  },
  header: {
    backgroundColor: theme.palette.background.default,
    alignItems: 'center',
  },
  backdrop: {
    zIndex: theme.zIndex.tooltip + 1,
    color: '#fff',
  },
}));

function useWindowSize() {
  const [size, setSize] = useState([0, 0]);
  useLayoutEffect(() => {
    function updateSize() {
      setSize([window.innerWidth, window.innerHeight]);
    }
    window.addEventListener('resize', updateSize);
    updateSize();
    return () => window.removeEventListener('resize', updateSize);
  }, []);
  return size;
}

export const searchPath = ({
  page = 1,
  size = 50,
  q,
  order,
  dir,
  asAtDate,
}) => {
  const params = new URLSearchParams();
  params.append('q', q);
  if (order) { params.append('order', order); }
  if (dir) { params.append('dir', dir); }
  if (asAtDate) { params.append('asAtDate', asAtDate); }

  return `/app/search/${page}/${size}?${params.toString()}`;
};

const QueueList = forwardRef((props, ref) => {
  const classes = useStyles();
  const {
    queueId,
    page = 1,
    recordsPerPage = 50,
  } = useParams();
  const dispatch = useDispatch();
  const { queues, tickets } = useSelector((state) => state.queue);
  const [loaded, setLoaded] = useState(false);
  const [rowsPerPage, setRowsPerPage] = useState();
  const [queuePage, setQueuePage] = useState();
  const [csvData, setCSVData] = useState([]);
  const [, windowHeight] = useWindowSize();
  const history = useHistory();
  const tableRef = useRef();
  const csvRef = useRef();
  const [columns] = useLocalStorage('searchColumns');

  const [selectedRow, setSelectedRow] = useState(null);
  const [colourDialogOpen, setColourDialogOpen] = useState(false);
  const [selectedColour, setSelectedColour] = useState('');
  const [backdropOpen, setBackdropOpen] = useState(false);

  const {
    q, order, dir, asAtDate,
  } = useQuery(window.location.search);

  const getPath = (
    newPage,
    newSize,
    newQ = q,
    newOrder = order,
    newDir = dir,
    newAsAtDate = asAtDate,
  ) => searchPath({
    page: newPage,
    size: newSize,
    q: newQ,
    order: newOrder,
    dir: newDir,
    asAtDate: newAsAtDate,
  });

  useImperativeHandle(ref, () => ({
    exportGrid() {
      const data = [];

      const header = [];
      for (let index = 0; index < tickets.allColumns.length; index++) {
        const column = tickets.allColumns[index];
        header.push(column.value);
      }

      data.push(header);

      for (let index = 0; index < tickets.allTickets.length; index++) {
        const ticket = tickets.allTickets[index];

        const row = [];
        const push = (col) => {
          if (typeof col === 'string') {
            row.push(col.replace(/"/g, '""'));
          } else {
            row.push(col);
          }
        };

        for (let x = 0; x < tickets.allColumns.length; x++) {
          const column = tickets.allColumns[x];
          if (column.key === 'Type') {
            push(ticket.type);
          } else if (column.key === 'Priority') {
            push(ticket.priority);
          } else if (column.key === 'Status') {
            push(ticket.status);
          } else if (column.key === 'Reporter') {
            push(`${ticket.reporter.firstName} ${ticket.reporter.lastName}`);
          } else if (column.key === 'Assignee') {
            if (ticket.assignee.id) {
              push(`${ticket.assignee.firstName} ${ticket.assignee.lastName}`);
            } else {
              push('Unassigned');
            }
          } else if (column.key === 'Organisation') {
            push(ticket.organisation);
          } else if (column.key === 'LoggedTime') {
            push(ticket.loggedTime);
          } else {
            push(ticket[column.key.charAt(0).toLowerCase() + column.key.slice(1)]);
          }
        }
        data.push(row);
      }

      setCSVData(data);

      setTimeout(() => {
        csvRef.current.link.click();
      }, 300);
    }

  }));

  useEffect(() => {
    if (!q && !queueId) {
      return;
    }

    (async function loadData() {
      setLoaded(false);
      await dispatch(getTickets({
        queueId,
        q,
        order,
        dir,
        columns,
        asAtDate,
        page: queuePage || page,
        recordsPerPage: rowsPerPage || recordsPerPage,
      }));

      setLoaded(true);
    }());
  }, [
    dispatch,
    queueId,
    q,
    order,
    dir,
    columns,
    page,
    asAtDate,
    queuePage,
    recordsPerPage,
    rowsPerPage,
  ]);

  useEffect(() => {
    if (!q && !queueId) {
      return undefined;
    }

    const interval = setInterval(() => {
      dispatch(getTickets({
        queueId,
        q,
        order,
        dir,
        columns,
        asAtDate,
        page: queuePage || page,
        recordsPerPage: rowsPerPage || recordsPerPage,
      }));
    }, 30000);

    return () => clearInterval(interval);
  }, [dispatch,
    queueId,
    q,
    order,
    dir,
    columns,
    page,
    queuePage,
    asAtDate,
    recordsPerPage,
    rowsPerPage,
  ]);

  const handleChangePage = (event, newPage) => {
    if (q) {
      history.push(getPath(newPage + 1, recordsPerPage));
    } else {
      setQueuePage(newPage + 1);
    }
  };

  const handleChangeRowsPerPage = (event) => {
    if (q) {
      history.push(getPath(page, event.target.value));
    } else {
      setRowsPerPage(+event.target.value);
    }
  };

  const columnClick = (column) => {
    if (q) {
      let columnName = column.key;

      if (columnName.toLowerCase() === 'reporter') {
        columnName = 'ReporterName';
      } else if (columnName.toLowerCase() === 'assignee') {
        columnName = 'AssigneeName';
      }

      const newDir = order === columnName && dir.toUpperCase() === 'ASC'
        ? 'DESC'
        : 'ASC';
      history.push(getPath(1, recordsPerPage, q, columnName, newDir));
    }
  };

  const handleColourClose = (save) => {
    if (save) {
      setBackdropOpen(true);
      axios.put(`/api/ticket/${selectedRow.id}/colour`, null, {
        params: {
          colour: selectedColour,
        }
      })
        .then(() => {
          dispatch(getTickets({
            queueId,
            q,
            order,
            dir,
            columns,
            asAtDate,
            page: queuePage || page,
            recordsPerPage: rowsPerPage || recordsPerPage,
          }));
          setColourDialogOpen(false);
          setSelectedRow(null);
          setBackdropOpen(false);
        })
        .catch((error) => {
          console.error(error);
          setColourDialogOpen(false);
          setSelectedRow(null);
          setBackdropOpen(false);
        });
    } else {
      setColourDialogOpen(false);
      setSelectedRow(null);
    }
  };

  const setRowColour = (row) => {
    setSelectedRow(row);
    setColourDialogOpen(true);
    setSelectedColour(row.highlightRowColour);
  };

  const clearRowColour = (row) => {
    setBackdropOpen(true);
    axios.put(`/api/ticket/${row.id}/colour`, null, {
      params: {
        colour: null,
      }
    })
      .then(() => {
        dispatch(getTickets({
          queueId,
          q,
          order,
          dir,
          columns,
          asAtDate,
          page: queuePage || page,
          recordsPerPage: rowsPerPage || recordsPerPage,
        }));
        setBackdropOpen(false);
      })
      .catch((error) => {
        console.error(error);
        setBackdropOpen(false);
      });
  };

  return (
    <div className={classes.root}>

      <Dialog
        open={colourDialogOpen}
        onClose={() => handleColourClose(false)}
      >
        <DialogContent className={classes.colour}>
          <CirclePicker color={selectedColour} onChange={(color) => setSelectedColour(color.hex)} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => handleColourClose(true)} color="secondary" variant="contained">
            Save
          </Button>
          <Button onClick={() => handleColourClose(false)} color="primary" autoFocus>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      <Backdrop className={classes.backdrop} open={backdropOpen}>
        <CircularProgress color="inherit" />
      </Backdrop>

      {!loaded || queues === undefined
        ? (
          <Grid
            container
            direction="column"
            justify="center"
            alignItems="center"
          >
            <CircularProgress style={{ marginTop: window.innerHeight / 2 }} />
          </Grid>
        )
        : (
          <div>
            {!q
              ? (
                <Toolbar
                  queueName={queues.filter((x) => x.id.toString() === (queueId === 'all' ? 0 : queueId))[0]?.name ?? ''}
                  tickets={tickets.allIds.length}
                />
              )
              : null}
            <Divider />
            <CSVLink
              data={csvData}
              filename="search.csv"
              className="hidden"
              ref={csvRef}
              target="_blank"
            />

            <TableContainer style={{ height: windowHeight - 188 }}>
              <Table size="small" stickyHeader ref={tableRef}>
                {q && tickets.allTickets.length === 0 && tickets.parserErrors.length === 0 && (
                  <div>
                    <Typography
                      style={{ color: 'red', marginLeft: 10 }}
                      variant="subtitle2"
                    >
                      {/* eslint-disable-next-line */}
                      No results found, try searching using wildcard eg: Text ~ 'search value'

                    </Typography>
                  </div>
                )}
                {q && tickets.parserErrors.length !== 0 && (
                  <div>
                    <Typography
                      style={{ color: 'red', marginLeft: 10 }}
                      variant="subtitle2"
                    >
                      Invalid query:
                    </Typography>
                    {tickets.parserErrors.map((e) => (
                      <Typography
                        style={{ color: 'red', marginLeft: 10 }}
                        variant="subtitle2"
                      >
                        {e.line}
                        :
                        {e.column}
                        {' '}
                        {e.message}
                      </Typography>
                    ))}
                  </div>
                )}
                {(!q || tickets.allTickets.length !== 0) && (
                  <TableHead className={classes.header}>
                    <TableRow>
                      {tickets.allColumns.map((column) => (
                        <TableCell key={column.key}>
                          <Link
                            component="button"
                            variant="body2"
                            color="textSecondary"
                            style={{ fontWeight: 500 }}
                            onClick={() => columnClick(column)}
                          >
                            {column.value}
                          </Link>
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                )}
                <TableBody>
                  {tickets.allTickets.map((currentTicket) => (
                    <QueueItem
                      ticket={currentTicket}
                      key={currentTicket.id}
                      allColumns={tickets.allColumns}
                      setRowColour={setRowColour}
                      clearRowColour={clearRowColour}
                    />
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <Divider />
            <TablePagination
              rowsPerPageOptions={[50, 100, 150]}
              component="div"
              count={tickets.recordCount}
              rowsPerPage={rowsPerPage || Number(recordsPerPage)}
              page={(queuePage || Number(page)) - 1}
              onChangePage={handleChangePage}
              onChangeRowsPerPage={handleChangeRowsPerPage}
            />
          </div>
        )}
    </div>
  );
});

export default QueueList;
